import NumericTextField from '@/components/form/numeric-text-field';
import { isValidNumeric, parseNumberInput } from '@/utils/number';
import { HelpText, Label, TextField } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import type { ThresholdSettingsTouchedField } from '../types';
import { linkedEnhancer } from './linked-enhancer';

type ThresholdFieldPrefix = 'above' | 'below';

type ThresholdFieldProps = {
  label: string;
  minValue: string;
  maxValue: string;
  idPrefix: ThresholdFieldPrefix;
  disabled?: boolean;
  hasError?: boolean;
  isLinked?: boolean;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  onFieldBlur: (field: ThresholdSettingsTouchedField) => void;
};

const isMinExceedingMax = (min: string, max: string): boolean => {
  if (!min || !max) return false;
  return parseNumberInput(min) > parseNumberInput(max);
};

export const ThresholdField = ({
  label,
  minValue,
  maxValue,
  idPrefix,
  disabled = false,
  hasError = false,
  isLinked = false,
  onMinChange,
  onMaxChange,
  onFieldBlur,
}: ThresholdFieldProps) => {
  const minExceedsMax = isMinExceedingMax(minValue, maxValue);
  const isMissingMin = !isValidNumeric(minValue);
  const isMissingMax = !isValidNumeric(maxValue);

  const getHelpText = () => {
    if (minExceedsMax) return 'Min must not exceed max';
    if (isMissingMin || isMissingMax) return 'Both min and max are required';
    return undefined;
  };

  const helpText = getHelpText();

  return (
    <Flex flexDirection="column" gap={2} width="100%">
      <Flex minHeight="24px" alignItems="center">
        <Label>{label}</Label>
      </Flex>
      <Flex alignItems="center" gap={2} width="100%">
        <NumericTextField
          disabled={disabled}
          validationState={hasError ? 'invalid' : undefined}
          value={minValue}
          renderStartEnhancer={() => (
            <TextField.Enhancer iconName="control-minus" aria-label="Min" />
          )}
          id={`${idPrefix}-min`}
          placeholder="min"
          onChange={(_e, value) => onMinChange(value)}
          onBlur={() => onFieldBlur(`${idPrefix}ThresholdMin`)}
          renderEndEnhancer={linkedEnhancer(isLinked)}
          required
        />
        <NumericTextField
          disabled={disabled}
          validationState={hasError ? 'invalid' : undefined}
          value={maxValue}
          renderStartEnhancer={() => (
            <TextField.Enhancer iconName="control-plus" aria-label="Max" />
          )}
          id={`${idPrefix}-max`}
          placeholder="max"
          onChange={(_e, value) => onMaxChange(value)}
          onBlur={() => onFieldBlur(`${idPrefix}ThresholdMax`)}
          renderEndEnhancer={linkedEnhancer(isLinked)}
          required
        />
      </Flex>
      {hasError && helpText && (
        <HelpText variant="negative">{helpText}</HelpText>
      )}
    </Flex>
  );
};
